import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SearchIcon from 'assets/icons/search_icon.svg?react';
import ClearIcon from '@mui/icons-material/Clear';
import professionsEndpoint from 'endPoints/professions';
import { getSkillInfo } from 'actions/profession';
import useDataListFactory from 'services/dataList';
import LinearProgress from 'components/LinearProgress';
import { COLUMNS_LIMIT_DEFAULT, IS_SNAP, SNAP_SHOT_LIMIT } from 'variables/main';
import { getLanguage } from 'translation';
import { isLocalizedDomain, getLocalizedPath } from 'helpers/getLocale';
import { generateItemListSchema, getBaseUrl, getSkillSlug } from 'utils/seo';

const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));
const Helmet = React.lazy(() => import('components/Helmet'));
const Header = React.lazy(() => import('components/Header'));
const Footer = React.lazy(() => import('components/Footer'));
const MainLayout = React.lazy(() => import('layouts/MainLayout'));
const Cards = React.lazy(() => import('components/Card'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Background = styled('section')(({ theme }) => ({
  background: `linear-gradient(to bottom, ${theme.palette.grey[50]}, ${theme.palette.white.main})`
}));

const SkillDetailWrapper = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  boxSizing: 'border-box',
  minHeight: 'calc(100vh - 235px)',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 40,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36.4px',
    marginBottom: 25
  }
}));

const ActionsWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 40
}));

const TextFieldStyled = styled(TextField)(() => ({
  borderRadius: 8
}));

const ButtonStyled = styled(Button)(() => ({
  display: 'block',
  margin: '0 auto'
}));

const ClearIconStyled = styled(ClearIcon)(({ theme }) => ({
  cursor: 'pointer',
  marginRight: 10,
  color: theme.palette.textPrimary.main
}));

const Description = styled(Typography)(() => ({
  marginBottom: 40,
  fontSize: 21,
  lineHeight: '30px'
}));

const SkillDetail = () => {
  const { id, slug } = useParams();
  const dispatch = useDispatch();
  const [skillInfo, setSkillInfo] = React.useState(null);
  const [lang] = React.useState(() => getLanguage().toUpperCase());
  const t = useTranslate('Skills');

  const { actions, data, loading, page, perPage, count, filters } = useDataList(
    professionsEndpoint,
    {
      page: 1,
      perPage: IS_SNAP ? SNAP_SHOT_LIMIT : COLUMNS_LIMIT_DEFAULT,
      filters: {
        skill_ids: [id]
      }
    }
  );

  React.useEffect(() => {
    const fetchSkillInfo = async () => {
      try {
        // getSkillInfo resolves with null on failure, so result is the data or null.
        const result = await dispatch(getSkillInfo(id));
        setSkillInfo(result);
      } catch (error) {
        setSkillInfo(null);
      }
    };

    if (id) {
      fetchSkillInfo();
    }
  }, [dispatch, id]);

  // Stale-slug redirect. Mirror the profession page: if the URL slug doesn't
  // match the expected slug for the current locale (e.g. a Ukrainian slug
  // landing on /de-de/skills/...), bounce to the canonical locale URL so
  // Google folds duplicates together.
  React.useEffect(() => {
    if (!skillInfo || !id) return;
    const expectedSlug = getSkillSlug(skillInfo);
    if (expectedSlug && slug && expectedSlug !== slug) {
      window.location.replace(getLocalizedPath(`/skills/${expectedSlug}/${id}`));
    }
  }, [skillInfo, slug, id]);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleChangeSearch = React.useCallback(
    (e) => {
      actions.onSearchChange(e.target.value);
    },
    [actions]
  );

  const onKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const searchValue = e.target.value;
        actions.onFilterChange({ ...filters, name: searchValue }, true);
      }
    },
    [actions, filters]
  );

  const handleClearSearch = React.useCallback(() => {
    actions.clearData(true);
  }, [actions]);

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const skillName = React.useMemo(() => {
    if (!skillInfo) return '';
    // Backend stores skill names lowercase ("analytical thinking"); capitalize
    // the first character for the breadcrumb, h1, and SEO meta so the page
    // doesn't read as mid-sentence fragments.
    const raw = skillInfo[`name${lang}`] || skillInfo.name || 'Skill';
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, [skillInfo, lang]);

  const breadcrumbs = React.useMemo(() => {
    if (!skillInfo) return [];

    return [
      {
        label: 'Home',
        link: '/'
      },
      {
        label: 'Skills Directory',
        link: '/skills'
      },
      {
        label: skillName || 'Skill',
        // `raw: true` tells BreadCrumbs to use the string verbatim instead of
        // looking it up as `Breadcrumbs.<label>` (dynamic skill names like
        // "analytical thinking" have no static translation key).
        raw: true
      }
    ];
  }, [skillName, skillInfo]);

  // On profitroll.com.ua: noindex skills without Ukrainian translation (e.g. Polish/Lithuanian content)
  const shouldNoIndex = React.useMemo(() => {
    if (isLocalizedDomain()) return false; // Jobalto: index all
    if (!skillInfo) return false; // Loading: let default indexing apply
    const hasUkrainian = !!(skillInfo.nameUA || skillInfo.nameUk);
    return !hasUkrainian;
  }, [skillInfo]);

  // Generate structured data
  const baseUrl = React.useMemo(() => getBaseUrl(), []);
  
  const skillSchema = React.useMemo(() => {
    if (!data || data.length === 0) return null;
    return generateItemListSchema(data, 'profession', baseUrl, '/professions');
  }, [data, baseUrl]);

  return (
    <>
      <Helmet
        page={'skill'}
        name={skillName}
        schema={skillSchema}
        breadcrumbs={breadcrumbs}
        includeHreflang={true}
        noIndex={shouldNoIndex}
      />
      <MainLayout>
        <Header />
        <Background>
          <SkillDetailWrapper>
            <Breadcrumbs breadcrumbs={breadcrumbs} />

            <Title variant="h1">{t('OccupationsWith', { skill: skillName })}</Title>

            <Description>
              {skillInfo?.description || t('OccupationsDescription')}
            </Description>

            <ActionsWrapper>
              <TextFieldStyled
                value={filters?.name || ''}
                onChange={handleChangeSearch}
                onKeyDown={onKeyDown}
                placeholder={t('SearchProfessions')}
                variant="outlined"
                fullWidth
                slotProps={{
                  htmlInput: {
                    'aria-label': t('SearchProfessions')
                  },
                  input: {
                    endAdornment: (
                      <>
                        {filters?.name ? <ClearIconStyled onClick={handleClearSearch} /> : null}
                        <SearchIcon />
                      </>
                    )
                  }
                }}
              />
            </ActionsWrapper>

            <Cards list={data} endPoint="professions" />

            {data && data.length === 0 ? <Typography>{t('NoProfessionsFound')}</Typography> : null}

            {loading || !data ? <LinearProgress /> : null}

            {limitReached ? null : (
              <>
                <ButtonStyled
                  variant="contained"
                  color="primary"
                  onClick={handleChangePage}
                  aria-label={t('LoadMore')}>
                  {t('LoadMore')}
                </ButtonStyled>
              </>
            )}
          </SkillDetailWrapper>
        </Background>

        <Footer />
      </MainLayout>
    </>
  );
};

export default SkillDetail;
